import { Link } from 'react-router-dom'

import { COMPANY_NUMBER, ENTITY, ENTITY_ADDRESS, LEGAL_UPDATED, supportEmail } from '../legal'

/** Terms of service. Public: reachable signed in or out. */
export default function Terms() {
  const email = supportEmail()
  return (
    <>
      <h1>Terms of service</h1>
      <p className="muted">Last updated {LEGAL_UPDATED}</p>

      <p>
        These terms are between you and {ENTITY} ("we", "us"), a company registered in England
        and Wales (number {COMPANY_NUMBER}) whose registered office is {ENTITY_ADDRESS}. They
        apply when you create an account or use Flightdeck, including through its API or an app
        connected to your account. By creating an account you agree to them. Our{' '}
        <Link to="/privacy">privacy notice</Link> explains how we handle personal data.
      </p>

      <h2>The service</h2>
      <p>
        Flightdeck lets you write flashcard decks, attach images, and revise them in learn or
        test mode. You must be at least 13 to use it. If you are under 18, you may buy a paid plan
        only with the agreement of a parent or guardian, who is then responsible for the
        purchase. You are responsible for keeping your sign-in details safe and for what happens under your
        account. Tell us at once if you think someone else has access.
      </p>

      <h2>Plans and payment</h2>
      <ul>
        <li>
          The free plan costs nothing. Paid plans are monthly subscriptions in pounds sterling,
          at the prices shown on the Account page when you subscribe. Prices exclude tax; any
          VAT or similar tax that applies is added at checkout.
        </li>
        <li>
          Payment is taken by Stripe and renews every month until you cancel. You can change
          plan or cancel at any time from the Account page. A cancellation takes effect at the
          end of the period you have paid for; we do not refund part-months.
        </li>
        <li>
          If you move to a smaller plan or cancel, nothing is deleted. Decks beyond your new
          limit are locked: still visible, but read-only, and still counted against your
          allowance until you delete some decks and unlock others.
        </li>
        <li>
          If you are a consumer in the UK you have a statutory right to cancel within 14 days
          of first subscribing. Because the service starts straight away, if you cancel in that
          period we will refund what you paid less a fair amount for the days you used it.
          Email <a href={`mailto:${email}`}>{email}</a> to do so.
        </li>
        <li>
          We may change our prices or plans. We will give you at least 30 days' notice by email,
          and you can cancel before the change applies.
        </li>
      </ul>

      <h2>Limits</h2>
      <p>
        Each plan has limits on decks, images, storage, and daily uploads, image views and quiz
        sessions. They are shown on the Account page and enforced automatically. We may also
        limit unusually heavy use to protect the service for others.
      </p>

      <h2>Your content</h2>
      <p>
        You keep ownership of everything you upload. You give us permission to store, process
        and display it, including making resized copies of images and checking them
        automatically, only so far as needed to run the service for you. Your decks and images
        are private to your account. You promise that you have the right to use what you upload.
      </p>

      <h2>Acceptable use</h2>
      <p>
        Flightdeck is for revision material. Do not upload sexually explicit material, anything
        that sexualises minors, hate symbols or propaganda, or anything else that is illegal
        where you or we are. Do not use the service to attack, overload or reverse-engineer it,
        to resell access, or to get round your plan's limits or use another person's account.
      </p>
      <p>
        Uploaded images are checked automatically and rejected if they break these rules. If
        you break them, we may remove content and suspend or close your account, with or
        without notice where the breach is serious. Illegal material is preserved and reported
        to the relevant authorities. To report abuse, see the contact details on the home page.
      </p>

      <h2>Connected apps and the API</h2>
      <p>
        You can allow an AI assistant or other app to act on your account. What it does there is
        done with your permission and counts as your use, within your plan's limits. You can see
        and revoke each connected app on the Account page.
      </p>

      <h2>Inactive decks</h2>
      <p>
        To keep storage in check, decks you have not opened for a long time are flagged, then
        archived, and eventually deleted, with email warnings along the way. The timings are in
        the <Link to="/privacy">privacy notice</Link>. Archived decks can be restored while they
        exist.
      </p>

      <h2>Availability and changes</h2>
      <p>
        We work to keep Flightdeck running but cannot promise it will always be available or
        free of errors, and we may change or retire features. Keep your own copy of anything
        you cannot afford to lose: your decks can be read back at any time through the API.
      </p>

      <h2>Our responsibility to you</h2>
      <p>
        Nothing in these terms limits liability that cannot be limited by law, including for
        death or personal injury caused by negligence, fraud, or your rights as a consumer. Subject
        to that, we are not liable for indirect or consequential loss, or for loss of data,
        profit or business, and our total liability for anything arising from the service in any
        12 months is limited to what you paid us in that period.
      </p>

      <h2>Ending your account</h2>
      <p>
        You can delete your account at any time from the Account page, which cancels your
        subscription and removes your data. We may suspend or close an account for breach of
        these terms, or close the service with at least 30 days' notice, refunding any
        prepaid period.
      </p>

      <h2>Changes to these terms</h2>
      <p>
        We may update these terms. For a change that matters we will email you at least 30 days
        before it applies; continuing to use the service afterwards means you accept it.
      </p>

      <h2>General</h2>
      <p>
        These terms are governed by the law of England and Wales, and its courts have
        jurisdiction, although if you live elsewhere in the UK you may also use your local
        courts. Contact us at <a href={`mailto:${email}`}>{email}</a>.
      </p>
    </>
  )
}
